import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarX, Plus, Loader2, Trash2, ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Standalone Leaves page for the assigned Interior Designer.
 * Also usable by Design Manager for their team via `?designer=<uid>` param.
 * Adding a leave triggers backend auto-push for any active project whose
 * Next Target Date is on/after the leave day.
 */
export default function DesignerLeavesPage() {
  const nav = useNavigate();
  const { principal, roleName } = useAuth();
  // If this page ever gets a query param for a specific designer we'll pull
  // it here; for now default to the logged-in designer.
  const params = new URLSearchParams(window.location.search);
  const designerId = params.get("designer") || principal?.id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!designerId) return;
    try {
      const { data } = await api.get(`/designers/${designerId}/leaves`);
      setRows(data.leaves || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [designerId]);

  useEffect(() => { load(); }, [load]);

  const addLeave = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    if (reason.trim().length < 2) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/designers/${designerId}/leaves`, {
        date, reason: reason.trim(),
      });
      const pushed = data.pushed_count || 0;
      toast.success(
        pushed
          ? `Leave saved · rolled ${pushed} project${pushed === 1 ? "" : "s"} forward`
          : "Leave saved"
      );
      setDate(""); setReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add leave");
    } finally { setBusy(false); }
  };

  const removeLeave = async (leaveId) => {
    setBusy(true);
    try {
      await api.delete(`/designers/${designerId}/leaves/${leaveId}`);
      toast.success("Leave removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)}
                  className="iz-btn-secondary text-xs flex items-center gap-1.5"
                  data-testid="leaves-back">
            <ArrowLeft className="w-3.5 h-3.5"/> Back
          </button>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design · Personal</p>
            <h1 className="font-display text-2xl md:text-3xl text-white mt-1">My Leaves</h1>
            <p className="text-[11px] text-gray-500 mt-1 italic">
              Marking a leave rolls all active project target dates on/after
              that day by one working day.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-10 py-6 space-y-5">
        {/* Add leave form */}
        <div className="iz-card p-4 space-y-3" data-testid="add-leave-form">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5"/> Mark New Leave
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="iz-input text-xs" data-testid="leave-date"/>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="Reason (required)"
                   className="iz-input text-xs sm:col-span-2"
                   data-testid="leave-reason"/>
          </div>
          <button onClick={addLeave} disabled={busy}
                  className="iz-btn-primary text-xs flex items-center gap-1.5"
                  data-testid="leave-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}
            Save Leave
          </button>
        </div>

        {/* Leaves list */}
        <div className="iz-card p-4" data-testid="leaves-list">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1">
            <CalendarX className="w-3.5 h-3.5"/> Upcoming &amp; Past Leaves ({rows.length})
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No leaves yet.</p>
          ) : (
            <ul className="space-y-1">
              {rows.map((l) => (
                <li key={l.id}
                    className="flex items-center gap-3 border border-[#161616] rounded px-3 py-2 text-xs"
                    data-testid={`leave-row-${l.id}`}>
                  <span className="text-white font-mono min-w-[100px]">{l.date}</span>
                  <span className="text-gray-400 flex-1 truncate">{l.reason}</span>
                  <span className="text-gray-600 text-[10px]">
                    {l.created_by_name || ""}
                  </span>
                  <button onClick={() => removeLeave(l.id)} disabled={busy}
                          className="text-gray-500 hover:text-red-400"
                          data-testid={`leave-delete-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
